/**
 * API functions for Ideas endpoints
 */

import { request } from './client'
import type {
  Idea,
  CreateIdeaRequest,
  UpdateIdeaRequest,
  IdeasListResponse,
  AnchorsResponse,
} from './types'

/**
 * Create a new idea
 *
 * @param data - The idea data to create
 * @returns Promise resolving to the created idea
 */
export async function createIdea(data: CreateIdeaRequest): Promise<Idea> {
  return request<Idea>('/api/ideas', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Get all ideas with optional filters
 *
 * @param filters - Optional filters for status and category
 * @returns Promise resolving to the list of ideas
 */
export async function getIdeas(filters?: {
  status?: string
  category?: string
}): Promise<IdeasListResponse> {
  // Build query string from filters
  const params = new URLSearchParams()
  if (filters?.status) {
    params.append('status', filters.status)
  }
  if (filters?.category) {
    params.append('category', filters.category)
  }

  const queryString = params.toString()
  const path = queryString ? `/api/ideas?${queryString}` : '/api/ideas'

  return request<IdeasListResponse>(path, {
    method: 'GET',
  })
}

/**
 * Get a single idea by ID
 *
 * @param id - The idea ID
 * @returns Promise resolving to the idea
 */
export async function getIdea(id: string): Promise<Idea> {
  return request<Idea>(`/api/ideas/${id}`, {
    method: 'GET',
  })
}

/**
 * Update an existing idea
 *
 * @param id - The idea ID
 * @param data - The fields to update
 * @returns Promise resolving to the updated idea
 */
export async function updateIdea(
  id: string,
  data: UpdateIdeaRequest
): Promise<Idea> {
  return request<Idea>(`/api/ideas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Delete an idea
 *
 * @param id - The idea ID
 * @returns Promise resolving when the idea is deleted
 */
export async function deleteIdea(id: string): Promise<void> {
  return request<void>(`/api/ideas/${id}`, {
    method: 'DELETE',
  })
}

/**
 * Get anchor positions for the 3D graph
 * Returns real projected positions of semantic anchors
 *
 * @returns Promise resolving to anchor positions
 */
export async function getAnchors(): Promise<AnchorsResponse> {
  return request<AnchorsResponse>('/api/anchors', {
    method: 'GET',
  })
}
